import { useCallback, useRef, useState } from 'react';
import { agentAPI } from '../../services/api';

/**
 * Drives the SSE agent chat. Messages are:
 *   { id, role: 'user', text }
 *   { id, role: 'assistant', blocks: [...], error? }
 * Assistant blocks:
 *   { type: 'text', text }
 *   { type: 'tool', name, label, status: 'running'|'done'|'error' }
 *   { type: 'table', title, columns, rows }
 *   { type: 'chart', spec }
 *   { type: 'projects', title, payload, filters }
 *
 * onTurnComplete(title) fires after each finished turn (used to refresh the
 * chat-history sidebar).
 */
const useAgentStream = ({ onTurnComplete } = {}) => {
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const nextId = useRef(0);
  const abortRef = useRef(null);

  const updateAssistant = useCallback((updater) => {
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (last.role !== 'assistant') return prev;
      return [...prev.slice(0, -1), updater({ ...last, blocks: [...last.blocks] })];
    });
  }, []);

  const handleEvent = useCallback((event) => {
    switch (event.type) {
      case 'conversation':
        setConversationId(event.conversation_id);
        break;
      case 'text_delta':
        updateAssistant((msg) => {
          const last = msg.blocks[msg.blocks.length - 1];
          if (last && last.type === 'text') {
            msg.blocks[msg.blocks.length - 1] = { ...last, text: last.text + event.text };
          } else {
            msg.blocks.push({ type: 'text', text: event.text });
          }
          return msg;
        });
        break;
      case 'tool_start':
        updateAssistant((msg) => {
          msg.blocks.push({
            type: 'tool',
            name: event.name,
            label: event.label,
            status: 'running',
          });
          return msg;
        });
        break;
      case 'tool_end':
        updateAssistant((msg) => {
          for (let i = msg.blocks.length - 1; i >= 0; i -= 1) {
            const block = msg.blocks[i];
            if (block.type === 'tool' && block.name === event.name && block.status === 'running') {
              msg.blocks[i] = { ...block, status: event.is_error ? 'error' : 'done' };
              break;
            }
          }
          return msg;
        });
        break;
      case 'block':
        updateAssistant((msg) => {
          const { kind, ...rest } = event.block;
          msg.blocks.push({ type: kind, ...rest });
          return msg;
        });
        break;
      case 'error':
        updateAssistant((msg) => ({ ...msg, error: event.message }));
        break;
      case 'done':
        if (onTurnComplete) onTurnComplete(event.title);
        break;
      default:
        break;
    }
  }, [updateAssistant, onTurnComplete]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isStreaming) return;

    setMessages((prev) => [
      ...prev,
      { id: nextId.current++, role: 'user', text: trimmed },
      { id: nextId.current++, role: 'assistant', blocks: [] },
    ]);
    setIsStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await agentAPI.streamChat({
        conversationId,
        message: trimmed,
        onEvent: handleEvent,
        signal: controller.signal,
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        updateAssistant((msg) => ({
          ...msg,
          error: error.message || 'Verbindung zum Server fehlgeschlagen',
        }));
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [conversationId, handleEvent, isStreaming, updateAssistant]);

  /** Start a fresh chat. The previous chat stays in the history sidebar. */
  const startNewChat = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setConversationId(null);
    setMessages([]);
    setIsStreaming(false);
  }, []);

  /** Load a persisted chat from the history sidebar. */
  const loadConversation = useCallback(async (id) => {
    if (abortRef.current) abortRef.current.abort();
    setIsStreaming(false);
    const response = await agentAPI.getConversation(id);
    const { conversation_id: cid, messages: display } = response.data;
    setConversationId(cid);
    setMessages((display || []).map((msg) => ({ ...msg, id: nextId.current++ })));
  }, []);

  return {
    messages,
    conversationId,
    isStreaming,
    sendMessage,
    startNewChat,
    loadConversation,
  };
};

export default useAgentStream;
